import React, { useState } from 'react';
import { useSurvey } from '../../../context/SurveyContext';
import { ArrowLeft, Monitor, Smartphone, ChevronLeft, ChevronRight } from 'lucide-react';
import './SurveyPreview.css';

const SurveyPreview = ({ onBack }) => {
  const { currentSurvey } = useSurvey();
  const [mobileView, setMobileView] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showSubmitMessage, setShowSubmitMessage] = useState(false);

  const handlePreviewSubmit = () => {
    setShowSubmitMessage(true);
    setTimeout(() => setShowSubmitMessage(false), 3000);
  };

  if (!currentSurvey) return null;

  const currentQuestion = currentSurvey.questions[currentQuestionIndex];
  const totalQuestions = currentSurvey.questions.length;

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleCheckboxToggle = (questionId, optionId) => {
    const current = answers[questionId] || [];
    const updated = current.includes(optionId)
      ? current.filter(id => id !== optionId)
      : [...current, optionId];
    setAnswers(prev => ({ ...prev, [questionId]: updated }));
  };

  const renderQuestionInput = (question) => {
    switch (question.type) {
      case 'multipleChoice':
        return (
          <div className="preview-radio-group">
            {question.options?.map(opt => (
              <label key={opt.id} className="preview-radio-option">
                <input
                  type="radio"
                  name={question.id}
                  checked={answers[question.id] === opt.id}
                  onChange={() => handleAnswer(question.id, opt.id)}
                />
                <span className="radio-custom"></span>
                {opt.text}
              </label>
            ))}
          </div>
        );

      case 'checkboxes':
        return (
          <div className="preview-checkbox-group">
            {question.options?.map(opt => (
              <label key={opt.id} className="preview-checkbox-option">
                <input
                  type="checkbox"
                  checked={(answers[question.id] || []).includes(opt.id)}
                  onChange={() => handleCheckboxToggle(question.id, opt.id)}
                />
                <span className="checkbox-custom"></span>
                {opt.text}
              </label>
            ))}
          </div>
        );

      case 'dropdown':
        return (
          <select
            className="preview-select"
            value={answers[question.id] || ''}
            onChange={(e) => handleAnswer(question.id, e.target.value)}
          >
            <option value="">Select an option...</option>
            {question.options?.map(opt => (
              <option key={opt.id} value={opt.id}>{opt.text}</option>
            ))}
          </select>
        );

      case 'linearScale':
        const min = question.scaleMin || 1;
        const max = question.scaleMax || 5;
        return (
          <div className="preview-scale">
            <span className="scale-label">{question.minLabel || min}</span>
            <div className="scale-options">
              {Array.from({ length: max - min + 1 }, (_, i) => min + i).map(num => (
                <label key={num} className="scale-option">
                  <input
                    type="radio"
                    name={question.id}
                    checked={answers[question.id] === num}
                    onChange={() => handleAnswer(question.id, num)}
                  />
                  <span className="scale-number">{num}</span>
                </label>
              ))}
            </div>
            <span className="scale-label">{question.maxLabel || max}</span>
          </div>
        );

      case 'likertScale':
        return (
          <div className="preview-likert">
            {question.labels?.map((label, index) => (
              <label key={index} className="likert-option">
                <input
                  type="radio"
                  name={question.id}
                  checked={answers[question.id] === index}
                  onChange={() => handleAnswer(question.id, index)}
                />
                <span className="likert-radio"></span>
                <span className="likert-label">{label}</span>
              </label>
            ))}
          </div>
        );

      case 'shortAnswer':
        return (
          <input
            type="text"
            className="preview-text-input"
            placeholder="Your answer..."
            value={answers[question.id] || ''}
            onChange={(e) => handleAnswer(question.id, e.target.value)}
            maxLength={question.maxLength}
          />
        );

      case 'longAnswer':
        return (
          <textarea
            className="preview-textarea"
            placeholder="Your answer..."
            value={answers[question.id] || ''}
            onChange={(e) => handleAnswer(question.id, e.target.value)}
            maxLength={question.maxLength}
          />
        );

      case 'yesNo':
        return (
          <div className="preview-yesno">
            <label className="yesno-option">
              <input
                type="radio"
                name={question.id}
                checked={answers[question.id] === 'yes'}
                onChange={() => handleAnswer(question.id, 'yes')}
              />
              <span className="yesno-btn">Yes</span>
            </label>
            <label className="yesno-option">
              <input
                type="radio"
                name={question.id}
                checked={answers[question.id] === 'no'}
                onChange={() => handleAnswer(question.id, 'no')}
              />
              <span className="yesno-btn">No</span>
            </label>
          </div>
        );

      case 'email':
        return (
          <input
            type="email"
            className="preview-text-input"
            placeholder="email@example.com"
            value={answers[question.id] || ''}
            onChange={(e) => handleAnswer(question.id, e.target.value)}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            className="preview-text-input"
            placeholder="Enter a number..."
            value={answers[question.id] || ''}
            onChange={(e) => handleAnswer(question.id, e.target.value)}
            min={question.min}
            max={question.max}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="survey-preview-container">
      <div className="preview-toolbar">
        <button className="btn-back" onClick={onBack}>
          <ArrowLeft size={18} />
          Back to Editor
        </button>
        <div className="preview-view-toggle">
          <button 
            className={`view-btn ${!mobileView ? 'active' : ''}`}
            onClick={() => setMobileView(false)}
          >
            <Monitor size={18} />
            Desktop
          </button>
          <button 
            className={`view-btn ${mobileView ? 'active' : ''}`}
            onClick={() => setMobileView(true)}
          >
            <Smartphone size={18} />
            Mobile
          </button>
        </div>
      </div>

      <div className={`preview-frame ${mobileView ? 'mobile' : ''}`}>
        <div className="survey-preview">
          <div className="survey-header">
            <h1>{currentSurvey.title}</h1>
            {currentSurvey.description && (
              <p className="survey-description">{currentSurvey.description}</p>
            )}
          </div>

          <div className="progress-bar">
            <div 
              className="progress-fill"
              style={{ width: `${((currentQuestionIndex + 1) / totalQuestions) * 100}%` }}
            />
          </div>
          <div className="progress-text">
            Question {currentQuestionIndex + 1} of {totalQuestions}
          </div>

          {currentQuestion && (
            <div className="question-container">
              <h2 className="question-text-preview">
                {currentQuestion.text}
                {currentQuestion.required && <span className="required">*</span>}
              </h2>
              {currentQuestion.helpText && (
                <p className="help-text">{currentQuestion.helpText}</p>
              )}
              {renderQuestionInput(currentQuestion)}
            </div>
          )}

          <div className="preview-nav">
            <button
              className="nav-btn prev"
              onClick={() => setCurrentQuestionIndex(i => Math.max(0, i - 1))}
              disabled={currentQuestionIndex === 0}
            >
              <ChevronLeft size={18} />
              Previous
            </button>
            {currentQuestionIndex < totalQuestions - 1 ? (
              <button
                className="nav-btn next"
                onClick={() => setCurrentQuestionIndex(i => Math.min(totalQuestions - 1, i + 1))}
              >
                Next
                <ChevronRight size={18} />
              </button>
            ) : (
              <button className="nav-btn submit" onClick={handlePreviewSubmit}>
                Submit (Preview)
              </button>
            )}
          </div>

          {showSubmitMessage && (
            <div className="preview-submit-message">
              <div className="submit-message-content">
                <span className="submit-icon">✓</span>
                <p>This is a preview. To collect real responses, distribute your survey using the "Distribute" tab.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SurveyPreview;
